import { ReactNode, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar } from '@/components/ui/avatar'
import { useAuth } from '@/auth'
import { useStore } from '@/store'
import { 
  X, ChevronLeft, ChevronRight, User, Settings
} from 'lucide-react'
import { 
  TrendsSection, 
  AnalyticsSection, 
  CreatorCopilotSection, 
  SavedItemsSection 
} from '@/components/sidebar'

function Sidebar() {
  const location = useLocation()
  const { user } = useAuth()
  const { sidebarOpen, setSidebarOpen, sidebarCollapsed, toggleSidebarCollapsed } = useStore()

  const handleItemClick = () => {
    setSidebarOpen(false)
  }

  return (
    <>
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 transform bg-card border-r transition-all duration-300 ease-in-out md:relative md:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
          sidebarCollapsed ? "w-16" : "w-64"
        )}
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className={cn(
            "h-16 items-center px-4 border-b",
            sidebarCollapsed ? "flex justify-center" : "grid grid-cols-3"
          )}>
            {!sidebarCollapsed && <div></div>}
            {!sidebarCollapsed && (
              <Link 
                to="/" 
                className="text-center leading-tight hover:opacity-80 transition-opacity cursor-pointer"
                onClick={handleItemClick}
              >
                <h1 className="text-lg font-bold">Narrio</h1>
                <p className="text-sm font-normal text-muted-foreground -mt-1">by GSX</p>
              </Link>
            )}
            <div className={cn(
              "flex items-center gap-1",
              sidebarCollapsed ? "justify-center" : "justify-end"
            )}>
              <Button
                variant="ghost"
                size="icon"
                className="hidden md:flex h-8 w-8"
                onClick={toggleSidebarCollapsed}
              >
                {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden h-8 w-8"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Main Navigation Sections */}
          <nav className="flex-1 space-y-6 p-3 overflow-y-auto">
            {/* Trends Section */}
            <TrendsSection 
              sidebarCollapsed={sidebarCollapsed} 
              onItemClick={handleItemClick} 
            />

            {/* Analytics Section */}
            <AnalyticsSection 
              sidebarCollapsed={sidebarCollapsed} 
              onItemClick={handleItemClick} 
            />

            {/* Creator Copilot Section */}
            <CreatorCopilotSection 
              sidebarCollapsed={sidebarCollapsed} 
              onItemClick={handleItemClick} 
            />
          </nav>

          {/* Saved Items Section */}
          <div className="border-t pt-4 pb-4 px-3">
            <SavedItemsSection 
              sidebarCollapsed={sidebarCollapsed} 
              onItemClick={handleItemClick} 
            />
          </div>

          {/* Bottom Section - User & Settings */}
          <div className="border-t p-3">
            {!sidebarCollapsed ? (
              <div className="flex items-center justify-between px-3 py-2">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Avatar className="h-8 w-8">
                    <div className="h-full w-full bg-primary/10 flex items-center justify-center">
                      <User className="h-4 w-4" />
                    </div>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {user?.name || user?.username}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
                <Link to="/settings">
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-8 w-8",
                      location.pathname === '/settings' && "bg-accent text-accent-foreground"
                    )}
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            ) : (
              <Link
                to="/settings"
                className="flex justify-center"
                title="Settings"
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-8 w-8",
                    location.pathname === '/settings' && "bg-accent text-accent-foreground"
                  )}
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </>
  )
}



interface LayoutProps {
  children: ReactNode
}

export function Layout({ children }: LayoutProps) {
  const { theme } = useStore()

  useEffect(() => {
    const root = window.document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(theme)
  }, [theme])

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <main className="flex-1 overflow-auto p-6">
          {children}
        </main>
      </div>
    </div>
  )
} 